import {
  ARTICLES_ENFORCED_LABEL,
  ARTICLES_RULE_SET_LINK_LABEL,
  ARTICLES_RULE_SET_PATH,
  ARTICLES_SECTION_SUBTITLE,
  ARTICLES_SECTION_TITLE,
  FLAGSHIP_FRAMEWORK,
  FRAMEWORKS_SECTION_TITLE,
  LAWS_PANEL_SUBTITLE,
  LAWS_PANEL_TITLE,
} from '../trustCopy';
import { getWebsiteUrl } from '../config';

/**
 * Chips are tags, sized to their text (h-8, the same on every row), so 14
 * articles read as a list rather than a wall of buttons. Tone says blocked
 * (emerald) vs screened (copper).
 */
const CHIP =
  'inline-flex h-8 items-center rounded-full border px-3.5 text-sm font-medium';
const CAUGHT = `${CHIP} border-emerald-500/40 bg-emerald-500/10 text-emerald-300`;
const SCREENED = `${CHIP} border-primary/35 bg-primary/10 text-primary`;
// Same label tier as the live status panel (TrustLiveStatusPanel). leading-8
// on md matches the chip height, so a label sits level with its first chip row.
const LABEL =
  'font-mono text-[11px] uppercase tracking-[0.14em] text-gray-400 md:leading-8';
// One row = label column + content column on md, stacked below it. Every row
// and the header and footer share the same side padding.
const PAD = 'px-6 md:px-8';
const ROW = `${PAD} grid gap-x-8 gap-y-3 border-t border-white/10 py-6 md:grid-cols-[15rem_minmax(0,1fr)]`;

/**
 * Laws and articles in one panel (2026-10-30). Leo: "fix spacing, padding and
 * alignment ... it doesnt look professional or trusted". Built like the live
 * status panel: a header, then hairline-separated rows with one label column,
 * so every label and every list starts on the same two left edges. The
 * rule set link ends the coverage note (Leo: "why is that link in the footer").
 * Data: frameworks_screened (rules that fired in 30 days, warnings included),
 * articles_enforced (blocks only), articles_screened (the rule set's coverage,
 * the same for every tenant). Renders nothing when all three are empty.
 */
export function TrustLawsSection({
  frameworks,
  articlesEnforced,
  articlesScreened,
}: {
  frameworks: string[];
  articlesEnforced: string[];
  articlesScreened: string[];
}) {
  // A blocked article is always shown, even if the screened list lacks it.
  const articles = [
    ...articlesScreened,
    ...articlesEnforced.filter((a) => !articlesScreened.includes(a)),
  ];
  if (frameworks.length === 0 && articles.length === 0) {
    return null;
  }
  const blocked = new Set(articlesEnforced);
  const laws = [
    ...frameworks.filter((f) => f === FLAGSHIP_FRAMEWORK),
    ...frameworks.filter((f) => f !== FLAGSHIP_FRAMEWORK),
  ];

  return (
    <section
      data-testid="trust-laws-section"
      className="trust-panel mt-6 overflow-hidden rounded-2xl"
    >
      <div className={`${PAD} py-6 md:py-8`}>
        <h2 className="font-display text-xl font-semibold text-white md:text-2xl">
          {LAWS_PANEL_TITLE}
        </h2>
        <p className="mt-2 max-w-[72ch] text-sm leading-relaxed text-gray-300">
          {LAWS_PANEL_SUBTITLE}
        </p>
      </div>

      {laws.length > 0 ? (
        <div className={ROW}>
          <h3 className={LABEL}>{FRAMEWORKS_SECTION_TITLE}</h3>
          <ul className="flex flex-wrap gap-2">
            {laws.map((law) => (
              <li key={law} className={CAUGHT}>
                {law}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {articles.length > 0 ? (
        <div data-testid="trust-articles-section" className={ROW}>
          <h3 className={LABEL}>{ARTICLES_SECTION_TITLE}</h3>
          <div>
            <ul className="flex flex-wrap gap-2">
              {articles.map((article) => (
                <li
                  key={article}
                  className={blocked.has(article) ? CAUGHT : SCREENED}
                >
                  {article}
                  {blocked.has(article) ? (
                    <span className="sr-only"> ({ARTICLES_ENFORCED_LABEL})</span>
                  ) : null}
                </li>
              ))}
            </ul>
            {blocked.size > 0 ? (
              <p className="mt-4 flex items-center gap-2 text-sm text-gray-300">
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-full border border-emerald-500/60 bg-emerald-500/30"
                />
                {ARTICLES_ENFORCED_LABEL}
              </p>
            ) : null}
            {/* The rule set link closes the note it explains, not a row of its own. */}
            <p
              className={`${blocked.size > 0 ? 'mt-2' : 'mt-4'} max-w-[72ch] text-sm leading-relaxed text-gray-400`}
            >
              {ARTICLES_SECTION_SUBTITLE}{' '}
              <a
                data-testid="trust-articles-rule-set-link"
                href={`${getWebsiteUrl()}${ARTICLES_RULE_SET_PATH}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary transition-colors hover:underline"
              >
                {ARTICLES_RULE_SET_LINK_LABEL}
              </a>
            </p>
          </div>
        </div>
      ) : null}
    </section>
  );
}
